<template>
  <v-card v-if="canSee" :outlined="!embedded" :flat="embedded" :color="embedded ? 'transparent' : undefined" class="mb-4">
    <v-card-title class="py-3" :class="{ 'px-0 pt-0': embedded }">
      <span v-if="!embedded" class="text-subtitle-1 font-weight-medium mr-3">{{ title || $t('onboarding.commissioning.orders') }}</span>
      <span class="text-caption text--secondary">{{ $t('onboarding.commissioning.summary', { open: openCount, done: doneCount }) }}</span>
      <v-spacer />
      <v-tooltip bottom :disabled="!orderingBlocked">
        <template #activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <v-btn v-if="canOrder" small :outlined="embedded" color="primary" :disabled="orderingBlocked" @click="ordering = true">
              <v-icon small left>mdi-plus</v-icon>{{ $t('onboarding.commissioning.new') }}
            </v-btn>
          </div>
        </template>
        <span>{{ blockedReason }}</span>
      </v-tooltip>
    </v-card-title>
    <v-divider />

    <v-alert v-if="error" type="error" dense text class="ma-3">{{ error }}</v-alert>

    <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="5"
        :no-data-text="$t('onboarding.commissioning.none')"
        dense
    >
      <template #item.kind="{ item }">
        <v-icon small class="mr-1">{{ kindIcon(item.kind) }}</v-icon>
        <router-link :to="orderRoute(item)" class="font-weight-medium">{{ kindOf(item) }}</router-link>
        <div class="text-caption text--secondary">
          <span v-if="item.requestedBy && item.requestedBy.name">{{ item.requestedBy.name }}</span>
          <span v-else>{{ $t(`onboarding.commissioning.origin.${item.origin}`) }}</span>
          · {{ item.createdAt | formatDate }}
        </div>
      </template>
      <template #item.beneficiary="{ item }">{{ beneficiaryOf(item) }}</template>
      <!-- Ce qui est posé ou activé, vers sa fiche -->
      <template #item.equipment="{ item }"><order-equipment-links :order="item" fallback="-" class="text-body-2" /></template>
      <!-- La file où elle attend (celle des « Commandes à traiter »), depuis quand ; un clic l'y ouvre -->
      <template #item.requirements="{ item }">
        <template v-if="item.step && !['done', 'closed'].includes(item.step)">
          <router-link :to="queueRoute(item)" class="text-body-2">{{ $t(`onboarding.queue.step.${item.step}`) }}</router-link>
          <div class="text-caption text--secondary">{{ $t('onboarding.commissioning.step-since', { date: $options.filters.formatDate(item.stepSince) }) }}</div>
        </template>
        <span v-else class="text--disabled">-</span>
      </template>
      <template #item.status="{ item }">
        <v-chip x-small :color="statusColor(item.status)" dark>{{ statusLabel(item.status) }}</v-chip>
        <div v-if="item.installation" class="text-caption text--secondary">{{ $t(`onboarding.commissioning.installation.${item.installation}`) }}</div>
      </template>
    </v-data-table>

    <equipment-order-dialog
        v-if="canOrder"
        v-model="ordering"
        :organization-id="organizationId"
        :holder="holder"
        :member="member"
        :vehicle="vehicle"
        :kinds="kinds"
        :kinds-for-organization-only="kindsForOrganizationOnly"
        :preset-who="presetWho"
        @created="load"
    />
  </v-card>
</template>

<script>
import CommissioningRepository from '@repository/CommissioningRepository'
import CommissioningMixin from '../CommissioningMixin'
import EquipmentOrderDialog from './EquipmentOrderDialog.vue'
import OrderEquipmentLinks from '../Order/OrderEquipmentLinks.vue'

/** La liste ne pagine pas : un point d'entrée (organisation, membre, véhicule) montre ses dernières commandes. */
const LIMIT = 100

/**
 * Les commandes d'équipement d'un point d'entrée — l'onglet Bornes ou Compteurs d'une organisation,
 * la fiche d'un membre, la fiche d'un véhicule — et le bouton pour en passer une, pré-remplie.
 */
export default {
  name: 'EquipmentOrdersCard',

  components: { EquipmentOrderDialog, OrderEquipmentLinks },

  mixins: [CommissioningMixin],

  props: {
    organizationId: { type: String, default: null },
    title: { type: String, default: null },
    /** Ce que le serveur filtre : { kind, holder, search }. */
    query: { type: Object, default: () => ({}) },
    /** Ce que le serveur ne sait pas filtrer (un membre, un véhicule) : gardé côté écran. */
    matches: { type: Function, default: null },
    holder: { type: String, required: true },
    member: { type: Object, default: null },
    vehicle: { type: Object, default: null },
    kinds: { type: Array, default: () => [] },
    /** Depuis l'organisation : les types ne bornent que la commande pour elle (voir EquipmentOrderDialog). */
    kindsForOrganizationOnly: { type: Boolean, default: false },
    /** Depuis l'organisation : la réponse à « Pour qui » proposée d'abord. */
    presetWho: { type: String, default: null },
    /** Un point d'entrée qui ne fait que montrer (le dossier d'une adhésion) : pas de bouton. */
    orderable: { type: Boolean, default: true },
    /** Pourquoi on ne peut pas commander d'ici (ex. aucun membre pour ce véhicule) ; null : on peut. */
    blockedReason: { type: String, default: null },
    /** Sur la fiche du membre, « Pour » le redirait à chaque ligne : la colonne disparaît. */
    hideBeneficiary: { type: Boolean, default: false },
    /** Dans la fenêtre d'un bouton : ni cadre ni titre (la fenêtre a le sien). */
    embedded: { type: Boolean, default: false },
  },

  data() {
    return { items: [], loading: false, error: null, ordering: false }
  },

  computed: {
    permissions() {
      return this.currentUser?.permissions || []
    },
    canOrder() {
      return this.orderable && this.permissions.includes('COMMISSIONING_REQUEST')
    },
    canSee() {
      return this.permissions.includes('COMMISSIONING_REQUEST') || this.permissions.includes('ONBOARDING_MODULE_MANAGE')
    },
    orderingBlocked() {
      return !!this.blockedReason || !this.organizationId
    },
    openCount() {
      return this.items.filter(this.isOpenOrder).length
    },
    doneCount() {
      return this.items.filter(i => i.status === 'in-service').length
    },
    headers() {
      // Pas de date prévue tant que la pose ne se planifie pas : la colonne reviendra avec elle.
      return [
        { text: this.$t('onboarding.commissioning.request'), value: 'kind', sortable: false },
        this.hideBeneficiary ? null : { text: this.$t('onboarding.commissioning.person'), value: 'beneficiary', sortable: false },
        { text: this.$t('onboarding.commissioning.equipment-column'), value: 'equipment', sortable: false },
        { text: this.$t('onboarding.commissioning.requirements-pending'), value: 'requirements', sortable: false },
        { text: this.$t('status'), value: 'status', sortable: false },
      ].filter(Boolean)
    },
  },

  watch: {
    organizationId: {
      immediate: true,
      handler() {
        this.load()
      },
    },
    query: {
      deep: true,
      handler() {
        this.load()
      },
    },
  },

  methods: {
    /** Depuis la fiche d'une organisation, on y reste ; ailleurs (membre, véhicule), les écrans généraux. */
    inOrganization() {
      return (this.$route.name || '').startsWith('organizations.') && !!this.organizationId
    },

    orderRoute(item) {
      return this.inOrganization()
        ? { name: 'organizations.equipment-order', params: { id: this.organizationId, orderId: item.id } }
        : { name: 'onboarding.commissionings.detail', params: { id: item.id } }
    },

    /** La file où la commande attend, la commande ouverte dans son tiroir. */
    queueRoute(item) {
      return this.inOrganization()
        ? { name: 'organizations.pending-orders', params: { id: this.organizationId }, query: { queue: item.step, order: item.id } }
        : { name: 'onboarding.commissionings', query: { queue: item.step, order: item.id } }
    },

    async load() {
      if (!this.canSee || !this.organizationId) {
        this.items = []
        return
      }
      this.loading = true
      this.error = null
      try {
        const data = await CommissioningRepository.list({ ...this.query, organizationId: this.organizationId, offset: 0, limit: LIMIT })
        this.items = this.matches ? data.items.filter(this.matches) : data.items
        this.$emit('counts', { open: this.openCount, done: this.doneCount })
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
